import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { API_BASE } from '../config/api';
import { 
  Check, 
  Crown, 
  Sparkles, 
  BookOpen, 
  Award, 
  BarChart3, 
  Bot,
  ArrowRight,
  Shield,
  Clock
} from 'lucide-react';

const API = `${API_BASE}/api`;

export default function PricingPage() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    fetchPlans();
    if (user && token) {
      fetchSubscriptionStatus();
    }
  }, [user, token]);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/subscription/plans`);
      setPlans(response.data.plans);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    }
  };

  const fetchSubscriptionStatus = async () => {
    try {
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    }
  };

  const handleStartTrial = async () => {
    if (!user) {
      navigate('/register?redirect=pricing');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/subscription/start-trial`,
        {},
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      toast.success(response.data.message);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start trial');
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (paymentMethod) => {
    if (!user) {
      navigate('/register?redirect=pricing');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/subscription/checkout`,
        {
          plan_id: 'individual_annual',
          payment_method: paymentMethod
        },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      // Redirect to payment gateway
      window.location.href = response.data.redirect_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate payment');
      setLoading(false);
    }
  };

  const features = [
    { icon: BookOpen, text: 'Access to all courses', description: 'Unlimited access to our complete course library' },
    { icon: Bot, text: 'NAVI AI Coach', description: 'Personalized AI-powered assessments and coaching' },
    { icon: Award, text: 'CPD Certificates', description: 'Earn recognized certificates for completed courses' },
    { icon: BarChart3, text: 'Progress Analytics', description: 'Track your learning journey with detailed insights' },
    { icon: Shield, text: 'Role-Based Learning', description: 'AI-driven learning paths aligned to your career' },
    { icon: Clock, text: 'Learn at Your Pace', description: 'Access content anytime, anywhere' }
  ];

  const isTrialActive = subscriptionStatus?.status === 'trial';
  const isSubscribed = subscriptionStatus?.status === 'active';
  const canStartTrial = !subscriptionStatus || subscriptionStatus.status === 'none';

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div 
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-amber-600 rounded-lg flex items-center justify-center">
              <Crown className="h-6 w-6 text-zinc-900" />
            </div>
            <span className="text-xl font-bold bg-gradient-to-r from-amber-400 to-amber-600 bg-clip-text text-transparent">
              Cerebro™
            </span>
          </div>
          <div className="flex items-center gap-4">
            {user ? (
              <button
                onClick={() => navigate('/dashboard')}
                className="text-zinc-400 hover:text-white transition-colors"
              >
                Dashboard
              </button>
            ) : (
              <>
                <button
                  onClick={() => navigate('/login')}
                  className="text-zinc-400 hover:text-white transition-colors"
                >
                  Sign In
                </button>
                <button
                  onClick={() => navigate('/register')}
                  className="bg-amber-500 hover:bg-amber-600 text-zinc-900 px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Get Started
                </button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-full px-4 py-2 mb-6">
            <Sparkles className="h-4 w-4 text-amber-400" />
            <span className="text-amber-400 text-sm font-medium">7-Day Free Trial</span>
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Invest in Your Professional Growth
          </h1>
          <p className="text-xl text-zinc-400 mb-8">
            Full access to all courses, AI-powered coaching, and CPD certificates. 
            Start your journey today.
          </p>
        </div>
      </section>

      {/* Pricing Card */}
      <section className="pb-20 px-4">
        <div className="max-w-lg mx-auto">
          <div className="relative bg-zinc-900 border-2 border-amber-500 rounded-2xl p-8 overflow-hidden">
            {/* Popular Badge */}
            <div className="absolute top-0 right-0 bg-amber-500 text-zinc-900 text-xs font-bold px-4 py-1 rounded-bl-lg">
              MOST POPULAR
            </div>

            {/* Plan Header */}
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold text-white mb-2">Annual Access</h2>
              <p className="text-zinc-400">Everything you need to succeed</p>
            </div>

            {/* Price */}
            <div className="text-center mb-8">
              <div className="flex items-baseline justify-center gap-2">
                <span className="text-5xl font-bold text-white">R2,999</span>
                <span className="text-zinc-400">/year</span>
              </div>
              <p className="text-sm text-zinc-500 mt-2">That's only R250/month</p>
            </div>

            {/* Features */}
            <div className="space-y-4 mb-8">
              {features.map((feature, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="h-4 w-4 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{feature.text}</p>
                    <p className="text-sm text-zinc-500">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            {isSubscribed ? (
              <div className="text-center">
                <div className="bg-green-500/20 border border-green-500/50 rounded-lg p-4 mb-4">
                  <p className="text-green-400 font-medium">You're subscribed!</p>
                  <p className="text-sm text-zinc-400 mt-1">
                    Valid until {new Date(subscriptionStatus.subscription_ends_at).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-zinc-900 py-3 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2"
                >
                  Go to Dashboard
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            ) : isTrialActive ? (
              <div className="space-y-4">
                <div className="bg-amber-500/20 border border-amber-500/50 rounded-lg p-4 text-center">
                  <p className="text-amber-400 font-medium">Trial Active</p>
                  <p className="text-sm text-zinc-400 mt-1">
                    {subscriptionStatus.days_remaining} days remaining
                  </p>
                </div>
                <button
                  onClick={() => handleSubscribe('payfast')}
                  disabled={loading}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-zinc-900 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
                >
                  {loading ? 'Processing...' : 'Subscribe Now - PayFast'}
                </button>
                <button
                  onClick={() => handleSubscribe('paypal')}
                  disabled={loading}
                  className="w-full bg-zinc-700 hover:bg-zinc-600 text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
                >
                  {loading ? 'Processing...' : 'Subscribe Now - PayPal'}
                </button>
              </div>
            ) : canStartTrial ? (
              <div className="space-y-4">
                <button
                  onClick={handleStartTrial}
                  disabled={loading}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-zinc-900 py-4 rounded-lg font-semibold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading ? 'Starting...' : (
                    <>
                      Start 7-Day Free Trial
                      <ArrowRight className="h-5 w-5" />
                    </>
                  )}
                </button>
                <p className="text-center text-sm text-zinc-500">
                  No credit card required • Cancel anytime
                </p>
                <div className="border-t border-zinc-800 pt-4">
                  <p className="text-center text-sm text-zinc-400 mb-3">Or subscribe now</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleSubscribe('payfast')}
                      disabled={loading}
                      className="bg-zinc-700 hover:bg-zinc-600 text-white py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      PayFast
                    </button>
                    <button
                      onClick={() => handleSubscribe('paypal')}
                      disabled={loading}
                      className="bg-zinc-700 hover:bg-zinc-600 text-white py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                    >
                      PayPal
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4 text-center">
                  <p className="text-red-400 font-medium">Trial Expired</p>
                  <p className="text-sm text-zinc-400 mt-1">Subscribe to continue learning</p>
                </div>
                <button
                  onClick={() => handleSubscribe('payfast')}
                  disabled={loading}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-zinc-900 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
                >
                  {loading ? 'Processing...' : 'Subscribe Now - PayFast'}
                </button>
                <button
                  onClick={() => handleSubscribe('paypal')}
                  disabled={loading}
                  className="w-full bg-zinc-700 hover:bg-zinc-600 text-white py-3 rounded-lg font-semibold transition-colors disabled:opacity-50"
                >
                  {loading ? 'Processing...' : 'Subscribe Now - PayPal'}
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Enterprise Section - Contact Sales */}
      <section className="py-20 px-4 border-t border-zinc-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Need Enterprise Access?</h2>
          <p className="text-zinc-400 mb-8">
            Deploy Cerebro™ as your own LMS-as-a-Service with custom branding, SSO integration, 
            and dedicated support. Contact us for a tailored solution.
          </p>
          <button
            onClick={() => window.location.href = 'mailto:enterprise@navigatecompliance.co.za?subject=Enterprise%20LMS%20Inquiry'}
            className="bg-zinc-800 hover:bg-zinc-700 text-white px-8 py-3 rounded-lg font-medium transition-colors border border-zinc-700"
          >
            Contact Sales
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-800 py-8">
        <div className="max-w-7xl mx-auto px-4 text-center text-zinc-500 text-sm">
          © 2026 Cerebro™. All rights reserved. • 
          <a href="/terms" className="hover:text-white ml-2">Terms</a> • 
          <a href="/privacy" className="hover:text-white ml-2">Privacy</a>
        </div>
      </footer>
    </div>
  );
}
